import React, { useState, useEffect } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button } from 'react-bootstrap';
import { useDeliveryReport } from '../services/api/hooks/useDeliveryReport';
import { useTopics } from '../services/api/hooks/useTopics';

const CampaignDeliveryReport: React.FC = () => {
  const { 
    data: reportData, 
    loading: isLoading, 
    error, 
    regenerating,
    fetchDeliveryReport,
    regenerateReport
  } = useDeliveryReport();
  
  const { data: topics } = useTopics();
  
  // Get default topic (always required)
  const defaultTopic = topics?.find((t: any) => t.is_default);
  const defaultTopicId = defaultTopic?.id || topics?.[0]?.id;
  
  const [selectedTopicId, setSelectedTopicId] = useState<number | undefined>(undefined);

  // Set default topic when topics load
  useEffect(() => {
    if (topics && topics.length > 0 && !selectedTopicId) {
      const defaultTopic = topics.find((t: any) => t.is_default);
      const defaultId = defaultTopic?.id || topics[0].id;
      setSelectedTopicId(defaultId);
    }
  }, [topics, selectedTopicId]);

  const [selectedDays, setSelectedDays] = useState(30);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [trigger, setTrigger] = useState(false);
  const [regenerateFromDate, setRegenerateFromDate] = useState('');

  const handleGenerateReport = () => {
    setTrigger(true);
  };

  const handleRegenerate = async () => {
    if (!regenerateFromDate) {
      alert('Please select a start date for regeneration');
      return;
    }
    
    const fromDate = new Date(regenerateFromDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    if (fromDate > today) {
      alert('Start date cannot be in the future');
      return;
    }
    
    const endDate = today.toISOString().split('T')[0];
    
    try {
      await regenerateReport({ 
        startDate: regenerateFromDate, 
        endDate: endDate
      });
      setRegenerateFromDate('');
      alert('Regeneration started successfully. Please refresh the report after a few moments.');
    } catch (err) {
      console.error('Error regenerating report:', err);
      alert('Failed to start regeneration');
    }
  };

  React.useEffect(() => {
    if (trigger) {
      if (showCustomDateRange) {
        if (!customStartDate || !customEndDate) {
          alert('Please select both start and end dates');
          setTrigger(false);
          return;
        }
        if (new Date(customStartDate) > new Date(customEndDate)) {
          alert('Start date must be before or equal to end date');
          setTrigger(false);
          return;
        }
        fetchDeliveryReport({ startDate: customStartDate, endDate: customEndDate, topicId: selectedTopicId });
      } else {
        fetchDeliveryReport({ days: selectedDays, topicId: selectedTopicId });
      }
      setTrigger(false);
    }
  }, [trigger, showCustomDateRange, customStartDate, customEndDate, selectedDays, selectedTopicId, fetchDeliveryReport]);

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const getStatusStyle = (value: number, type: 'delivery' | 'engagement' | 'issue'): React.CSSProperties => {
    if (type === 'delivery') {
      if (value >= 95) return { backgroundColor: '#d4edda', color: '#155724' };
      if (value >= 85) return { backgroundColor: '#fff3cd', color: '#856404' };
      return { backgroundColor: '#f8d7da', color: '#721c24' };
    } else if (type === 'engagement') {
      if (value >= 10) return { backgroundColor: '#d4edda', color: '#155724' };
      if (value >= 5) return { backgroundColor: '#d1ecf1', color: '#0c5460' };
      return {};
    } else if (type === 'issue') {
      if (value > 5) return { backgroundColor: '#f8d7da', color: '#721c24' };
      if (value > 0) return { backgroundColor: '#fff3cd', color: '#856404' };
      return { backgroundColor: '#d4edda', color: '#155724' };
    }
    return {};
  };

  return (
    <div>
      <Form>
        <Row className="mb-3">
          <Col md={3}>
            <Form.Label>Time Period:</Form.Label>
            <Form.Select
              value={showCustomDateRange ? 'custom' : selectedDays}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'custom') {
                  setShowCustomDateRange(true);
                } else {
                  setShowCustomDateRange(false);
                  setSelectedDays(Number(value));
                }
              }}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value="custom">Custom Range</option>
            </Form.Select>
          </Col>
          
          {showCustomDateRange && (
            <>
              <Col md={2}>
                <Form.Label>Start Date:</Form.Label>
                <Form.Control
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label>End Date:</Form.Label>
                <Form.Control
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </Col>
            </>
          )}
          
          <Col md={3}>
            <Form.Label>Topic:</Form.Label>
            <Form.Select
              value={selectedTopicId || defaultTopicId}
              onChange={(e) => setSelectedTopicId(parseInt(e.target.value))}
            >
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          
          <Col md={2} className="d-flex align-items-end">
            <Button 
              onClick={handleGenerateReport}
              disabled={isLoading}
              variant="primary"
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : "Generate Report"}
            </Button>
          </Col>

          <Col md={6}>
            <Form.Label>Regenerate From:</Form.Label>
            <Form.Control
              type="date"
              value={regenerateFromDate}
              onChange={(e) => setRegenerateFromDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button
              variant="warning"
              onClick={handleRegenerate}
              disabled={regenerating || !regenerateFromDate}
            >
              {regenerating ? 'Regenerating...' : 'Regenerate'}
            </Button>
          </Col>
        </Row>
      </Form>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading delivery report...</span>
          </Spinner>
        </div>
      )}

      {!isLoading && reportData.length > 0 && (
        <>
          <Alert variant="light" className="mb-3" style={{ border: '1px solid #dee2e6' }}>
            <strong>Legend</strong>
            <Row className="mt-2 g-2">
              <Col md={6}>
                <div><strong>Color Indicators:</strong></div>
                <div className="mt-1">
                  <span style={{ backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '3px', display: 'inline-block', minWidth: '70px', textAlign: 'center' }}>Green</span>
                  <span className="ms-2">Delivery ≥95% | Opens ≥10% | Clicks ≥10% | Bounced 0% | Unsubscribed 0%</span>
                </div>
                <div className="mt-1">
                  <span style={{ backgroundColor: '#d1ecf1', color: '#0c5460', padding: '3px 10px', borderRadius: '3px', display: 'inline-block', minWidth: '70px', textAlign: 'center' }}>Blue</span>
                  <span className="ms-2">Opens 5-9.99% | Clicks 5-9.99%</span>
                </div>
                <div className="mt-1">
                  <span style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '3px 10px', borderRadius: '3px', display: 'inline-block', minWidth: '70px', textAlign: 'center' }}>Yellow</span>
                  <span className="ms-2">Delivery 85-94.99% | Bounced 1-5% | Unsubscribed 1-5%</span>
                </div>
                <div className="mt-1">
                  <span style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '3px 10px', borderRadius: '3px', display: 'inline-block', minWidth: '70px', textAlign: 'center' }}>Red</span>
                  <span className="ms-2">Delivery &lt;85% | Bounced &gt;5% | Unsubscribed &gt;5%</span>
                </div>
              </Col>
              <Col md={6}>
                <div><strong>Trend Arrows:</strong></div>
                <div className="mt-1">
                  <span style={{ color: '#28a745', fontSize: '1.2rem', fontWeight: 'bold' }}>↑</span>
                  <span className="ms-2">Improvement vs previous day</span>
                </div>
                <div className="mt-1">
                  <span style={{ color: '#dc3545', fontSize: '1.2rem', fontWeight: 'bold' }}>↓</span>
                  <span className="ms-2">Decline vs previous day</span>
                </div>
                <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                  Arrows appear when change is ≥0.5%
                </div>
              </Col>
            </Row>
          </Alert>
          <div className="shadow rounded overflow-hidden">
          <div className="d-flex justify-content-end p-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                const header1 = ['Date', 'Campaign', 'Sent', 'Delivered', '', 'Opens', '', 'Clicks', '', 'Bounced', '', 'Unsubscribed', ''];
                const header2 = ['', '', '', 'Count', '%', 'Count', '%', 'Count', '%', 'Count', '%', 'Count', '%'];
                
                const rows = reportData.map((row: any) => [
                  row.date,
                  row.campaignName,
                  row.campaignsSent.toString(),
                  row.delivered.toString(),
                  row.deliveredPercent.toFixed(2),
                  row.opens.toString(),
                  row.opensPercent.toFixed(2),
                  row.clicks.toString(),
                  row.clicksPercent.toFixed(2),
                  row.bounced.toString(),
                  row.bouncedPercent.toFixed(2),
                  row.unsubscribed.toString(),
                  row.unsubscribedPercent.toFixed(2)
                ]);
                
                const csvContent = [header1, header2, ...rows].map(row => row.join('\t')).join('\n');
                navigator.clipboard.writeText(csvContent);
                alert('Table copied to clipboard!');
              }}
              title="Copy table data"
            >
              Copy
            </button>
          </div>
          <Table striped bordered hover responsive className="mb-0">
            <thead style={{ backgroundColor: '#0d6efd', color: 'white' }}>
              <tr>
                <th rowSpan={2} style={{ padding: '12px', verticalAlign: 'middle', width: '15%' }}>Date</th>
                <th rowSpan={2} style={{ padding: '12px', verticalAlign: 'middle' }}>Campaign</th>
                <th rowSpan={2} className="text-center" style={{ padding: '12px', verticalAlign: 'middle' }}>Sent</th>
                <th colSpan={2} className="text-center" style={{ padding: '12px' }}>Delivered</th>
                <th colSpan={2} className="text-center" style={{ padding: '12px' }}>Opens</th>
                <th colSpan={2} className="text-center" style={{ padding: '12px' }}>Clicks</th>
                <th colSpan={2} className="text-center" style={{ padding: '12px' }}>Bounced</th>
                <th colSpan={2} className="text-center" style={{ padding: '12px' }}>Unsubscribed</th>
              </tr>
              <tr>
                <th className="text-center" style={{ padding: '8px' }}>Count</th>
                <th className="text-center" style={{ padding: '8px' }}>%</th>
                <th className="text-center" style={{ padding: '8px' }}>Count</th>
                <th className="text-center" style={{ padding: '8px' }}>%</th>
                <th className="text-center" style={{ padding: '8px' }}>Count</th>
                <th className="text-center" style={{ padding: '8px' }}>%</th>
                <th className="text-center" style={{ padding: '8px' }}>Count</th>
                <th className="text-center" style={{ padding: '8px' }}>%</th>
                <th className="text-center" style={{ padding: '8px' }}>Count</th>
                <th className="text-center" style={{ padding: '8px' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row: any, index: number) => {
                const prevRow = index < reportData.length - 1 ? reportData[index + 1] : null;
                const deliveryChange = prevRow ? row.deliveredPercent - prevRow.deliveredPercent : 0;
                const opensChange = prevRow ? row.opensPercent - prevRow.opensPercent : 0;
                const clicksChange = prevRow ? row.clicksPercent - prevRow.clicksPercent : 0;
                
                return (
                <tr key={index} style={{ transition: 'all 0.2s' }}>
                  <td className="fw-bold" style={{ paddingLeft: '15px'}}>{row.date}</td>
                  <td className="text-truncate" title={row.campaignName} style={{ paddingLeft: '15px', maxWidth: '200px' }}>{row.campaignName}</td>
                  <td className="text-center">{formatNumber(row.campaignsSent)}</td>
                  <td className="text-center fw-bold" style={{ ...getStatusStyle(row.deliveredPercent, 'delivery') }}>
                    {formatNumber(row.delivered)}
                  </td>
                  <td className="text-center fw-bold" style={{ ...getStatusStyle(row.deliveredPercent, 'delivery') }}>
                    {row.deliveredPercent.toFixed(2)}% {prevRow && Math.abs(deliveryChange) >= 0.5 && <span style={{ color: deliveryChange > 0 ? '#28a745' : '#dc3545' }}>{deliveryChange > 0 ? '↑' : '↓'}</span>}
                  </td>
                  <td className="text-center" style={{ ...getStatusStyle(row.opensPercent, 'engagement') }}>
                    {formatNumber(row.opens)}
                  </td>
                  <td className="text-center" style={{ ...getStatusStyle(row.opensPercent, 'engagement') }}>
                    {row.opensPercent.toFixed(2)}% {prevRow && Math.abs(opensChange) >= 0.5 && <span style={{ color: opensChange > 0 ? '#28a745' : '#dc3545' }}>{opensChange > 0 ? '↑' : '↓'}</span>}
                  </td>
                  <td className="text-center" style={{ ...getStatusStyle(row.clicksPercent, 'engagement') }}>
                    {formatNumber(row.clicks)}
                  </td>
                  <td className="text-center" style={{ ...getStatusStyle(row.clicksPercent, 'engagement') }}>
                    {row.clicksPercent.toFixed(2)}% {prevRow && Math.abs(clicksChange) >= 0.5 && <span style={{ color: clicksChange > 0 ? '#28a745' : '#dc3545' }}>{clicksChange > 0 ? '↑' : '↓'}</span>}
                  </td>
                  <td className="text-center fw-semibold" style={{ ...getStatusStyle(row.bouncedPercent, 'issue') }}>
                    {formatNumber(row.bounced)}
                  </td>
                  <td className="text-center fw-semibold" style={{ ...getStatusStyle(row.bouncedPercent, 'issue') }}>
                    {row.bouncedPercent.toFixed(2)}%
                  </td>
                  <td className="text-center fw-semibold" style={{ ...getStatusStyle(row.unsubscribedPercent, 'issue') }}>
                    {formatNumber(row.unsubscribed)}
                  </td>
                  <td className="text-center fw-semibold" style={{ ...getStatusStyle(row.unsubscribedPercent, 'issue') }}>
                    {row.unsubscribedPercent.toFixed(2)}%
                  </td>
                </tr>
              );})}
            </tbody>
          </Table>
        </div>
        </>
      )}

      {!isLoading && reportData.length === 0 && !error && (
        <div className="text-center py-4">
          <p className="text-muted">
            Click "Generate Report" to view campaign delivery statistics.
          </p>
        </div>
      )}
    </div>
  );
};

export default CampaignDeliveryReport;
